import React, { useState, useMemo } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { MainLayout } from '@/components/layout/MainLayout'
import { ArrowLeft, AlertCircle, FileText, ShieldCheck } from 'lucide-react'
import { useGetCollaborationQuery } from '@/redux/marketerApi'
import { DealPerformanceCard } from '@/components/gifting/DealPerformanceCard'
import { SubmissionViewerDialog } from '@/components/SubmissionViewerDialog'
import { LicensingOfferDialog } from '@/components/LicensingOfferDialog'
import {
  CollaborationDetailsTabs,
  COLLABORATION_DETAIL_TABS,
  type CollaborationDetailsTab,
} from '@/components/collaboration/CollaborationDetailsTabs'
import api from '@/lib/api'
import { collabPath } from '@/lib/collabPath'

const actionClass =
  'inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted'

// Terms, due dates, files and history of one deal. The deal itself is run from the
// collaboration workspace; this page holds what the workspace links out to.
const CollaborationDetails = () => {
  const { campaignId, collaborationId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const tab = searchParams.get('tab')
  const activeTab: CollaborationDetailsTab = COLLABORATION_DETAIL_TABS.includes(
    tab as CollaborationDetailsTab,
  )
    ? (tab as CollaborationDetailsTab)
    : 'campaign'

  const handleTabChange = (next: CollaborationDetailsTab) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.set('tab', next)
      return params
    })
  }

  const {
    data: collaboration,
    isLoading: loading,
    error,
    refetch,
  } = useGetCollaborationQuery(collaborationId!, { skip: !collaborationId })

  const [submissionViewerOpen, setSubmissionViewerOpen] = useState(false)
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null)
  const [licensingDialogOpen, setLicensingDialogOpen] = useState(false)

  // The offer itself shows on the workspace; keep its sent and accepted events here
  const filteredTimeline = useMemo(() => {
    const items = collaboration?.timeline || []
    return items.filter((message) => {
      const meta = message.metadata as Record<string, unknown> | null
      const event = meta && typeof meta['event'] === 'string' ? meta['event'] : null
      if (event === 'offer_sent' || event === 'offer_accepted') return true
      return message.milestone?.slug !== 'agree_on_terms'
    })
  }, [collaboration?.timeline])

  const workspaceHref = collabPath(campaignId, collaborationId!, '')

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 py-16 text-center text-muted-foreground">Loading deal…</div>
      </MainLayout>
    )
  }

  if (error || !collaboration) {
    return (
      <MainLayout>
        <div className="mx-auto max-w-md p-6 py-16 text-center">
          <AlertCircle className="mx-auto mb-4 h-10 w-10 text-[#DC2626]" />
          <h1 className="text-3xl font-bold text-foreground">Deal not found</h1>
          <p className="mt-2 text-muted-foreground">We couldn't load this deal.</p>
          <Link
            to={campaignId ? `/campaign/${campaignId}` : '/campaigns'}
            className="mt-6 inline-flex rounded-xl bg-[#C0FD72] px-4 py-2 text-sm font-semibold text-[#0F172A]"
          >
            Back to campaigns
          </Link>
        </div>
      </MainLayout>
    )
  }

  const athlete = collaboration.athlete
  const campaign = collaboration.campaign

  return (
    <MainLayout>
      <div className="mx-auto w-full min-w-0 max-w-[1100px] space-y-4 p-4 sm:p-6">
        <Link
          to={workspaceHref}
          className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Back to collaboration
        </Link>

        <section className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5">
          <div className="flex min-w-0 items-center gap-3">
            {athlete?.avatar ? (
              <img
                src={athlete.avatar}
                alt=""
                className="h-12 w-12 shrink-0 rounded-full object-cover"
              />
            ) : (
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted font-semibold text-foreground">
                {athlete?.name?.[0] ?? '?'}
              </span>
            )}
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Deal details
              </p>
              <h1 className="text-3xl font-bold text-foreground">{athlete?.name ?? 'Athlete'}</h1>
              {campaign?.title ? (
                <p className="text-sm text-muted-foreground">{campaign.title}</p>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {collaboration.status === 'accepted' && !collaboration.licensingEnabled ? (
              <button className={actionClass} onClick={() => setLicensingDialogOpen(true)}>
                <FileText className="h-4 w-4" /> Add licensing rights
              </button>
            ) : null}
            {collaboration.licensingEnabled ? (
              <>
                <Link
                  to={collabPath(campaignId, collaborationId!, '/licensing')}
                  className={actionClass}
                >
                  <FileText className="h-4 w-4" /> Licensing details
                </Link>
                {/* Extensions are booked at the athlete's rate card, in the workspace */}
                <Link to={collabPath(campaignId, collaborationId!)} className={actionClass}>
                  <ShieldCheck className="h-4 w-4" /> Extend license
                </Link>
              </>
            ) : null}
          </div>
        </section>

        {/* Gifting: bonuses unlocked by views, paid usage left, one-click license extension */}
        {athlete && collaboration.performance?.terms ? (
          <DealPerformanceCard
            collaborationId={collaboration.id}
            athleteName={athlete.name}
            performance={collaboration.performance}
          />
        ) : null}

        <CollaborationDetailsTabs
          activeTab={activeTab}
          campaign={campaign}
          collaboration={collaboration}
          campaignId={campaignId}
          collaborationId={collaborationId}
          filteredTimeline={filteredTimeline}
          onTabChange={handleTabChange}
          onViewSubmission={(submissionId) => {
            setSelectedSubmissionId(submissionId)
            setSubmissionViewerOpen(true)
          }}
        />
      </div>

      {selectedSubmissionId && collaborationId && (
        <SubmissionViewerDialog
          open={submissionViewerOpen}
          onOpenChange={setSubmissionViewerOpen}
          collabId={collaborationId}
          submissionId={selectedSubmissionId}
        />
      )}

      {athlete && (
        <LicensingOfferDialog
          open={licensingDialogOpen}
          onOpenChange={setLicensingDialogOpen}
          athleteName={athlete.name}
          onConfirm={async (offer) => {
            await api.post(`/marketer/collaborations/${collaborationId}/enable-licensing`, {
              platforms: offer.platforms,
              message: offer.message,
            })
            refetch()
          }}
        />
      )}
    </MainLayout>
  )
}

export default CollaborationDetails
